import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Alert, 
  Spin, 
  Badge, 
  Progress, 
  Typography, 
  Space, 
  Divider,
  Tag,
  Timeline,
  Modal,
  notification,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  SyncOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  ClockCircleOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SafetyOutlined,
  RocketOutlined
} from '@ant-design/icons';
import api from '../utils/axios';

const { Title, Text } = Typography;

const SystemUpdate = () => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateLogs, setUpdateLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 检查更新
  const checkForUpdates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/update/check');
      
      if (response.data.success) {
        setUpdateInfo(response.data);
        
        // 检查更新后，同时刷新更新状态
        getUpdateStatus();
        
        if (response.data.hasUpdate) {
          notification.success({
            message: '发现新版本',
            description: `发现新版本 ${response.data.latestVersion}，请查看更新说明`,
            placement: 'topRight',
          });
        } else {
          // 没有更新时，强制重置更新状态
          setIsUpdating(false);
          setUpdateProgress(0);
          notification.info({
            message: '检查完成',
            description: '当前已是最新版本',
            placement: 'topRight',
          });
        }
      } else {
        // 处理业务逻辑错误
    
        notification.warning({
          message: '检查更新失败',
          description: response.data.message || '请稍后重试',
          placement: 'topRight',
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '网络连接失败';
      setError('检查更新失败: ' + errorMessage);
      notification.error({
        message: '检查更新失败',
        description: errorMessage,
        placement: 'topRight',
      });
          } finally {
        setLoading(false);
      }
    }, []);

  // 开始更新
  const startUpdate = useCallback(async () => {
    setError(null);
    setUpdateProgress(0);
    
    try {
      const response = await api.post('/update/start');
      
      if (response.data.success) {
        setIsUpdating(true);
        setShowConfirmModal(false);
        
        notification.success({
          message: '更新已开始',
          description: '系统正在后台进行更新，请耐心等待',
          placement: 'topRight',
        });
        
        // 开始轮询更新状态
        const interval = setInterval(async () => {
          try {
            const statusResponse = await api.get('/update/status');
            const { isUpdating: updating, recentLogs } = statusResponse.data;
            
            setIsUpdating(updating);
            setUpdateLogs(recentLogs);
            
            // 根据日志数量估算进度
            const progress = Math.min(95, recentLogs.length * 5);
            setUpdateProgress(progress);
            
            if (!updating) {
              clearInterval(interval);
              setUpdateProgress(100);
              
              // 更新完成后刷新页面状态
              setTimeout(async () => {
                await checkForUpdates();
                await getUpdateStatus();
                await fetchUpdateHistory();
                
                notification.success({
                  message: '更新完成',
                  description: '系统更新已完成，页面状态已刷新',
                  placement: 'topRight',
                });
              }, 1000);
            }
          } catch (error) {
            console.error('获取更新状态失败:', error);
          }
        }, 3000);
      }
    } catch (error) {
      setError('启动更新失败: ' + (error.response?.data?.message || error.message));
      notification.error({
        message: '启动更新失败',
        description: error.response?.data?.message || error.message,
        placement: 'topRight',
      });
    }
  }, []);

  // 获取更新历史
  const fetchUpdateHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/update/history');
      if (response.data.success) {
        setUpdateHistory(response.data.history || []);
      } else {
        console.error('获取更新历史失败:', response.data.message);
      }
    } catch (error) {
      console.error('获取更新历史失败:', error);
      // 如果API不存在，使用模拟数据
      setUpdateHistory([
        {
          id: 1,
          type: 'fix',
          title: '修复ESLint警告',
          description: '清理未使用的导入和变量，修复React Hook依赖问题',
          date: new Date().toISOString()
        },
        {
          id: 2,
          type: 'feature',
          title: '新增"当天新增"筛选条件',
          description: '在客户公海页面添加时间筛选功能，支持查看当天新增的客户',
          date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 3,
          type: 'update',
          title: '版本号延续修复',
          description: '修复自动发布脚本版本号问题，从1.5.7开始正确递增',
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        }
      ]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // 获取更新状态
  const getUpdateStatus = useCallback(async () => {
    try {
      const response = await api.get('/update/status');
      const { isUpdating: updating, recentLogs } = response.data;
      
      // 检查版本信息，如果版本相同且没有在更新，则确保状态为false
      if (updateInfo && !updateInfo.hasUpdate && updating) {
        // 版本相同但显示正在更新，这是异常状态，强制设置为false
        console.log('检测到异常状态：版本相同但显示正在更新，强制重置状态');
        setIsUpdating(false);
        setUpdateProgress(0);
      } else {
        setIsUpdating(updating);
      }
      
      setUpdateLogs(recentLogs);
    } catch (error) {
      console.error('获取更新状态失败:', error);
      // 如果获取状态失败，也重置更新状态
      setIsUpdating(false);
      setUpdateProgress(0);
    }
  }, []);

  useEffect(() => {
    checkForUpdates();
    getUpdateStatus();
    fetchUpdateHistory();
    
    // 延迟重置状态，确保没有卡住的更新状态
    const resetTimeout = setTimeout(() => {
      if (updateInfo && !updateInfo.hasUpdate) {
        setIsUpdating(false);
        setUpdateProgress(0);
      }
    }, 2000);
    
    // 定期刷新更新状态，确保状态同步
    const statusInterval = setInterval(getUpdateStatus, 5000);
    
    return () => {
      clearTimeout(resetTimeout);
      clearInterval(statusInterval);
    };
  }, [checkForUpdates, getUpdateStatus, fetchUpdateHistory]);

  // 解析日志条目
  const parseLogEntry = (logEntry) => {
    const match = logEntry.match(/\[(.*?)\] (.*)/);
    if (match) {
      return {
        timestamp: new Date(match[1]).toLocaleString('zh-CN'),
        message: match[2]
      };
    }
    return {
      timestamp: new Date().toLocaleString('zh-CN'),
      message: logEntry
    };
  };

  // 获取日志图标
  const getLogIcon = (message) => {
    if (message.includes('成功') || message.includes('完成')) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    } else if (message.includes('错误') || message.includes('失败')) {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    } else if (message.includes('步骤') || message.includes('开始')) {
      return <PlayCircleOutlined style={{ color: '#1890ff' }} />;
    } else {
      return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <RocketOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
          系统更新中心
        </Title>
        <Text type="secondary">保持系统最新，享受最佳体验</Text>
      </div>
        
      {error && (
        <Alert
          message="更新检查失败"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      <Row gutter={[24, 24]}>
        {/* 版本信息卡片 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <InfoCircleOutlined />
                版本信息
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<SyncOutlined spin={loading} />}
                onClick={checkForUpdates}
                loading={loading}
              >
                检查更新
              </Button>
            }
            style={{ height: '100%' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="当前版本"
                  value={updateInfo?.currentVersion || '未知'}
                  prefix={<Tag color="blue">v</Tag>}
                />
                {updateInfo?.currentVersionInfo?.source && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    来源: 本地版本
                  </Text>
                )}
              </Col>
              <Col span={12}>
                <Statistic
                  title="最新版本"
                  value={updateInfo?.latestVersion || '未知'}
                  prefix={<Tag color="green">v</Tag>}
                />
                {updateInfo?.latestVersionInfo?.publishedAt && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    发布时间: {new Date(updateInfo.latestVersionInfo.publishedAt).toLocaleDateString('zh-CN', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    })}
                  </Text>
                )}
              </Col>
            </Row>
            
            <Divider />
            
            <div style={{ textAlign: 'center' }}>
              {updateInfo?.updateStatus === 'available' ? (
                <Badge 
                  status="processing" 
                  text={
                    <Text strong style={{ color: '#1890ff' }}>
                      有新版本可用
                    </Text>
                  }
                />
              ) : updateInfo?.updateStatus === 'up-to-date' ? (
                <Badge 
                  status="success" 
                  text={
                    <Text strong style={{ color: '#52c41a' }}>
                      已是最新版本
                    </Text>
                  }
                />
              ) : updateInfo?.updateStatus === 'ahead' ? (
                <Badge 
                  status="warning" 
                  text={
                    <Text strong style={{ color: '#faad14' }}>
                      本地版本更新
                    </Text>
                  }
                />
              ) : (
                <Badge 
                  status="default" 
                  text={
                    <Text strong style={{ color: '#8c8c8c' }}>
                      状态未知
                    </Text>
                  }
                />
              )}
              
              {updateInfo?.statusMessage && (
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">{updateInfo.statusMessage}</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* 更新状态卡片 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                更新状态
              </Space>
            }
            style={{ height: '100%' }}
          >
            {isUpdating ? (
              <div>
                <Progress
                  percent={updateProgress}
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Spin size="small" />
                  <Text style={{ marginLeft: '8px' }}>正在更新系统...</Text>
                </div>
              </div>
            ) : updateInfo && !updateInfo.hasUpdate ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text strong>已是最新版本</Text>
                </div>
                <Text type="secondary">系统运行正常，无需更新</Text>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <SafetyOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text strong>系统状态正常</Text>
                </div>
                <Text type="secondary">随时可以开始更新</Text>
              </div>
            )}
          </Card>
        </Col>

        

        {/* 更新操作卡片 - 移到上面，方便用户操作 */}
        {updateInfo?.hasUpdate && (
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <RocketOutlined style={{ color: '#1890ff' }} />
                  更新操作
                </Space>
              }
              style={{ border: '2px solid #1890ff', backgroundColor: '#f6ffed' }}
            >
              <Row gutter={[24, 16]} align="middle">
                <Col xs={24} md={16}>
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong style={{ fontSize: '16px' }}>
                        发现新版本: {updateInfo.latestVersion}
                      </Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary">
                        发布时间: {updateInfo?.publishedAt ? 
                          new Date(updateInfo.publishedAt).toLocaleString('zh-CN') : 
                          '未知'
                        }
                      </Text>
                    </div>
                    
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlayCircleOutlined />}
                      onClick={() => setShowConfirmModal(true)}
                      disabled={isUpdating}
                      block
                    >
                      {isUpdating ? '更新进行中...' : '开始更新'}
                    </Button>
                    
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        预计需要 3-5 分钟
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        )}

        {/* 更新日志卡片 */}
        {isUpdating && (
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <ClockCircleOutlined />
                  更新进度
                </Space>
              }
            >
                             <div style={{ 
                 background: '#2d2d2d', 
                 color: '#ffffff', 
                 padding: '16px', 
                 borderRadius: '6px',
                 maxHeight: '400px',
                 overflowY: 'auto',
                 fontFamily: 'Monaco, Menlo, Consolas, monospace',
                 fontSize: '13px',
                 border: '1px solid #444',
                 boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
               }}>
                {updateLogs.length > 0 ? (
                  <Timeline
                    items={updateLogs.map((log, index) => {
                      const parsedLog = parseLogEntry(log);
                      return {
                        color: log.includes('成功') || log.includes('完成') ? 'green' : 
                               log.includes('错误') || log.includes('失败') ? 'red' : 'blue',
                                                 children: (
                           <div>
                             <Text style={{ color: '#aaa', fontSize: '12px' }}>
                               [{parsedLog.timestamp}]
                             </Text>
                             <Text style={{ marginLeft: '8px', color: '#ffffff' }}>
                               {parsedLog.message}
                             </Text>
                           </div>
                         )
                      };
                    })}
                  />
                                 ) : (
                   <div style={{ textAlign: 'center', padding: '20px' }}>
                     <Spin />
                     <div style={{ marginTop: '8px' }}>
                       <Text style={{ color: '#aaa' }}>等待更新日志...</Text>
                     </div>
                   </div>
                 )}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 更新记录卡片 - 移到页面底部，因为内容较长 */}
      <div style={{ marginTop: '24px' }}>
        <Card
          title={
            <Space>
              <ClockCircleOutlined />
              更新记录
            </Space>
          }
          extra={
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => fetchUpdateHistory()}
              size="small"
            >
              刷新
            </Button>
          }
        >
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin />
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">加载更新记录中...</Text>
              </div>
            </div>
          ) : (
            <>
              <Timeline
                items={updateHistory.map((item, index) => ({
                  color: item.type === 'feature' ? 'blue' : 
                         item.type === 'fix' ? 'green' : 
                         item.type === 'update' ? 'orange' : 'default',
                  children: (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <Text strong>{item.title}</Text>
                          {item.description && (
                            <div style={{ marginTop: '4px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {item.description}
                              </Text>
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Tag color={item.type === 'feature' ? 'blue' : 
                                     item.type === 'fix' ? 'green' : 
                                     item.type === 'update' ? 'orange' : 'default'}>
                            {item.type === 'feature' ? '新功能' : 
                             item.type === 'fix' ? 'bug修复' : 
                             item.type === 'update' ? '功能更新' : '其他'}
                          </Tag>
                          <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {new Date(item.date).toLocaleDateString('zh-CN', { 
                                year: 'numeric', 
                                month: '2-digit', 
                                day: '2-digit' 
                              })}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }))}
              />
              {updateHistory.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">暂无更新记录</Text>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* 确认更新模态框 */}
      <Modal
        title={
        <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            确认更新
          </Space>
        }
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            取消
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            danger
            icon={<PlayCircleOutlined />}
            onClick={startUpdate}
          >
            确认更新
          </Button>
        ]}
        width={500}
      >
        <Alert
          message="重要提醒"
          description={
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>更新过程中系统将暂时不可用</li>
              <li>请确保当前没有其他用户在使用系统</li>
              <li>更新会自动备份当前数据</li>
              <li>更新过程可能需要几分钟时间</li>
              <li>请勿关闭浏览器或刷新页面</li>
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Text>确定要开始更新吗？</Text>
      </Modal>
    </div>
  );
};

export default SystemUpdate;
